import { useQueryClient, type Query } from "@tanstack/react-query";
import { useEffect } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../env";
import { REPORT_QUERY_PREFIX } from "../reports/useReport";

export type Subscribe = (onEvent: () => void) => () => void;

/** ra-core list queries the dashboard reads (query key[0] is the resource). */
export const DASHBOARD_RESOURCES: readonly string[] = [
  "companies",
  "tasks",
  "deals",
  "v_my_queue_preview",
];
const isDashboardQuery = (q: Query) =>
  q.queryKey[0] === REPORT_QUERY_PREFIX ||
  DASHBOARD_RESOURCES.includes(String(q.queryKey[0]));

/** Trailing debounce of `ms`; a continuous stream still fires at most `maxWait` ms after its first event. */
export const createDebounced = (
  fn: () => void,
  ms: number,
  maxWait = Number.POSITIVE_INFINITY,
) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let firstAt: number | undefined;
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    firstAt = undefined;
  };
  const run = () => {
    const now = Date.now();
    firstAt ??= now;
    if (timer) clearTimeout(timer);
    const delay = Math.max(0, Math.min(ms, firstAt + maxWait - now));
    timer = setTimeout(() => {
      cancel();
      fn();
    }, delay);
  };
  return Object.assign(run, { cancel });
};

let channelSeq = 0;
/** Realtime on calls (insert/update) and deals (all); tables added to supabase_realtime in Task 11. */
export const supabaseSubscribe: Subscribe = (onEvent) => {
  const client = getSupabaseClient();
  const name = `gavion-dashboard-${Date.now().toString(36)}-${++channelSeq}`;
  const channel = client
    .channel(name)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "calls" },
      onEvent,
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "calls" },
      onEvent,
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "deals" },
      onEvent,
    )
    .subscribe((status, err) => {
      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT")
        console.warn(`[gavion-dashboard] realtime ${status}`, err ?? "");
    });
  return () => {
    void client.removeChannel(channel);
  };
};

/** No realtime in the demo, nor without a configured Supabase URL (component tests; as useRealtimeInvalidate). */
export const defaultSubscribe: Subscribe | null =
  IS_DEMO || !import.meta.env.VITE_SUPABASE_URL ? null : supabaseSubscribe;

/**
 * Spec 09 §4: counters refresh within 3 s of a new outcome (2 s debounce + one round trip), and at
 * least every 10 s during a burst. Only report queries and the dashboard's list resources are refetched.
 * No realtime in the demo.
 */
export const useLiveRefresh = (
  subscribe: Subscribe | null = defaultSubscribe,
  delayMs = 2000,
  maxWaitMs = 10_000,
) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!subscribe) return;
    const refresh = createDebounced(
      () => {
        void queryClient.invalidateQueries({ predicate: isDashboardQuery });
      },
      delayMs,
      maxWaitMs,
    );
    const unsubscribe = subscribe(refresh);
    return () => {
      refresh.cancel();
      unsubscribe();
    };
  }, [subscribe, delayMs, maxWaitMs, queryClient]);
};
